'use client'

import { createContext, useMemo, useState } from 'react'

import type { Media } from '../../../../types/FullProduct'

export interface ZoomContextType {
	media: Media[]
	select: (image?: Media) => void
	next: () => void
	prev: () => void
	image: Media | null
}

type ZoomProviderProps = {
	readonly media: Media[]
	readonly children: React.ReactNode
}

export const ZoomContext = createContext<ZoomContextType | undefined>(undefined)

export function ZoomProvider({
	children,
	media,
}: ZoomProviderProps): React.ReactNode {
	const [image, setImage] = useState<Media | null>(null)

	const select = (newMedia?: Media) => {
		setImage(newMedia || null)
	}

	const next = () => {
		if (!image) {
			return
		}
		const currentIndex = media.findIndex((item) => item === image)
		const nextIndex = currentIndex + 1
		if (nextIndex < media.length) {
			const nextImage = media[nextIndex]
			if (nextImage) {
				setImage(nextImage)
			}
		} else {
			setImage(media[0])
		}
	}

	const prev = () => {
		if (!image) {
			return
		}
		const currentIndex = media.findIndex((item) => item === image)
		const prevIndex = currentIndex - 1
		if (prevIndex >= 0) {
			const prevImage = media[prevIndex]
			if (prevImage) {
				setImage(prevImage)
			}
		} else {
			setImage(media.at(-1) as Media)
		}
	}

	const value = useMemo(
		() => ({ select, next, prev, media, image }),
		[image, media]
	)

	return <ZoomContext.Provider value={value}>{children}</ZoomContext.Provider>
}
